import { useEffect, useState } from 'react'

import { ActionIcon, Collapse, Select, TextInput, Badge } from '@mantine/core'
import { Check, X } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_ADMIN_COMPANY,
  UPDATE_ADMIN_COMPANY,
  UPDATE_LUMA_CREDENTIALS,
} from 'src/graphql/companies.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'

import FieldLabel from '../../companyTab/components/FieldLabel'

// Constants for select values
const SELECT_VALUES = {
  YES: 'yes',
  NO: 'no',
} as const

const SELECT_OPTIONS = [
  { value: SELECT_VALUES.YES, label: 'Enabled' },
  { value: SELECT_VALUES.NO, label: 'Disabled' },
] as const

// Helper functions for boolean conversion
const booleanToSelectValue = (value: boolean): string =>
  value ? SELECT_VALUES.YES : SELECT_VALUES.NO

const selectValueToBoolean = (value: string | null): boolean =>
  value === SELECT_VALUES.YES

const booleanToDisplayText = (value: boolean): string =>
  value ? 'Enabled' : 'Disabled'

const maskUsername = (username: string | null | undefined): string => {
  if (!username) return '-'
  if (username.length <= 2) return '*'.repeat(username.length)
  const firstChar = username[0]
  const lastChar = username[username.length - 1]
  const middleLength = Math.max(1, username.length - 2)
  return `${firstChar}${'*'.repeat(middleLength)}${lastChar}`
}

interface TrainingSettingsCardProps {
  companyData: {
    companyId: string
    trainingEnabled: boolean
    trainixCompanyId: string | null
    lumaUsername: string | null
    lumaPasswordSet: boolean
  }
  initialOpened?: boolean
}

const TrainingSettingsCard = ({
  companyData,
  initialOpened = false,
}: TrainingSettingsCardProps) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)

  const [values, setValues] = useState({ ...companyData })
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(initialOpened)
  const [newPassword, setNewPassword] = useState('')

  const [updateCompany, { loading }] = useMutation(UPDATE_ADMIN_COMPANY, {
    onCompleted: () => {
      toast('Training settings updated successfully', 'success')
      setEditing(false)
      setNewPassword('')
    },
    onError: (error) => {
      toast(`Failed to update settings: ${error.message}`, 'error')
    },
    refetchQueries: [
      {
        query: GET_ADMIN_COMPANY,
        variables: { id: values.companyId },
      },
    ],
    awaitRefetchQueries: true,
  })

  const [updateLumaCredentials, { loading: lumaLoading }] = useMutation(
    UPDATE_LUMA_CREDENTIALS,
    {
      onCompleted: () => {
        toast('Luma credentials updated successfully', 'success')
        setEditing(false)
        setNewPassword('')
      },
      onError: (error) => {
        toast(`Failed to update credentials: ${error.message}`, 'error')
      },
      refetchQueries: [
        {
          query: GET_ADMIN_COMPANY,
          variables: { id: values.companyId },
        },
      ],
      awaitRefetchQueries: true,
    }
  )

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      setValues({ ...companyData })
      setNewPassword('')
    }
  }, [companyData, editing])

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setValues({ ...companyData })
    setNewPassword('')
    setEditing(false)
  }

  const handleSave = async () => {
    const { companyId, lumaUsername, lumaPasswordSet, ...rest } = values

    // Update training settings only if changed
    const trainingInput: {trainingEnabled?: boolean; trainixCompanyId?: string | null } = {}
    if (values.trainingEnabled !== companyData.trainingEnabled) {
      trainingInput.trainingEnabled = values.trainingEnabled
    }
    if (values.trainixCompanyId !== companyData.trainixCompanyId) {
      trainingInput.trainixCompanyId = values.trainixCompanyId
    }

    if (Object.keys(trainingInput).length > 0) {
      await updateCompany({
        variables: {
          id: companyId,
          input: trainingInput,
        },
      })
    }

    // Update Luma credentials if changed
    const lumaInput: { lumaUsername?: string; lumaPassword?: string } = {}
    if (values.lumaUsername !== companyData.lumaUsername) {
      lumaInput.lumaUsername = values.lumaUsername || ''
    }
    if (newPassword) {
      lumaInput.lumaPassword = newPassword
    }

    if (Object.keys(lumaInput).length > 0) {
      await updateLumaCredentials({
        variables: {
          id: companyId,
          input: lumaInput,
        },
      })
    }
  }

  const trainingModeLabel = (
    <FieldLabel
      label="Training Mode"
      tooltip={
        <>
          Enable training features and tools for your company.
          <br />
          <br />
          When enabled, employees can assign trainings and courses to applicants
          through the Trainix integration.
        </>
      }
    />
  )

  const trainixCompanyIdLabel = (
    <FieldLabel
      label="Trainix Company ID"
      tooltip={
        <>
          The integration ID provided by Trainix platform for your company.
          <br />
          <br />
          This ID is used to sync trainings and courses from Trainix to your
          Double Nickel account.
        </>
      }
    />
  )

  const lumaUsernameLabel = (
    <FieldLabel
      label="Luma Username"
      tooltip={
        <>
          Your company's Luma platform login credentials for syncing trainings.
        </>
      }
    />
  )

  const lumaPasswordLabel = (
    <FieldLabel
      label="Luma Password"
      tooltip={
        <>
          Your company's Luma platform password.
        </>
      }
    />
  )

  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3`}
    >
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Training Settings
        {isAdmin && !editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {isAdmin && editing && (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading || lumaLoading}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading || lumaLoading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-4">
          <div className={`grid grid-cols-12 gap-3`}>
            {/* Training Mode */}
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={trainingModeLabel}
                  value={booleanToSelectValue(values.trainingEnabled ?? false)}
                  onChange={(value) =>
                    setValues({
                      ...values,
                      trainingEnabled: selectValueToBoolean(value),
                    })
                  }
                  data={SELECT_OPTIONS}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={trainingModeLabel}
                  value={booleanToDisplayText(values.trainingEnabled ?? false)}
                />
              )}
            </div>

            {/* Trainix Company ID */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={trainixCompanyIdLabel}
                  value={values.trainixCompanyId ?? ''}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      trainixCompanyId: e.target.value,
                    })
                  }
                  placeholder="Enter Trainix Company ID"
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={trainixCompanyIdLabel}
                  value={values.trainixCompanyId || '-'}
                />
              )}
            </div>
          </div>

          {/* Luma Credentials Section */}
          <div className="border-t border-doubleNickel-gray-200 pt-4">
            <div className="flex gap-6">
              {/* Luma Username */}
              <div className="flex-1">
                {editing ? (
                  <TextInput
                    label={lumaUsernameLabel}
                    value={values.lumaUsername ?? ''}
                    onChange={(e) =>
                      setValues({
                        ...values,
                        lumaUsername: e.target.value,
                      })
                    }
                    placeholder="Enter username"
                    className="w-full"
                    classNames={{
                      label: 'text-sm font-medium text-doubleNickel-gray-700',
                    }}
                  />
                ) : (
                  <StyledReadOnlyInput
                    label={lumaUsernameLabel}
                    value={maskUsername(values.lumaUsername)}
                  />
                )}
              </div>

              {/* Luma Password */}
              <div className="flex-1">
                {editing ? (
                  <TextInput
                    label={lumaPasswordLabel}
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (optional)"
                    className="w-full"
                    classNames={{
                      label: 'text-sm font-medium text-doubleNickel-gray-700',
                    }}
                  />
                ) : (
                  <StyledReadOnlyInput
                    label={lumaPasswordLabel}
                    value={
                      values.lumaPasswordSet ? (
                        <Badge variant="filled" size="sm" className="bg-doubleNickel-gray-200 text-doubleNickel-gray-700">
                          ●●●● Set
                        </Badge>
                      ) : (
                        <span className="text-doubleNickel-gray-500">
                          — Not set
                        </span>
                      )
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default TrainingSettingsCard
